import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { getAllSubcomponents } from "utils/enclosure/components";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import floorHeaderCodeTemplate from "features/Model/Enclosure/Templates/floorHeaderCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import MultiSelect from "components/Input/MultiSelect";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";
import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import { EmbodiedCarbonCalculationsSummary, StandardEmbodiedLayerBlock } from "components/EmbodiedCarbonGrade";

import globalStyles from "components/globalStyles.module.scss";
import CodeLibraryDestination from "components/CodeLibraryDestination";
// import wallCrossSection from "assets/images/Wall-Cross-Section.svg";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

const labelValidation = [required, charLim32];
const structureValidation = getValidation("csFloorHeaderStructureType");
const sheathingValidation = getValidation("csFloorHeaderSheathing");
const exteriorValidation = getValidation("csFloorHeaderExterior");
const ins1Validation = getValidation("csFloorHeaderInsLayer1");
const ins2Validation = getValidation("csFloorHeaderInsLayer2");

const codeSort = (a, b) => {
    if (a.label < b.label) {
        return -1;
    }
    if (a.label > b.label) {
        return 1;
    }
    return 0;
};

const StandardFloorHeaderCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { floorHeaderInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const floorHeaderCode = useStateSelector(`${drawerAccessor}.floorHeaderCode`) || {};
        const { id: floorHeaderStructureId, value: floorHeaderStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.floorHeaderCode.layers.structureType`) || {};
        const { value: floorHeaderIns1Value } =
            useStateSelector(`${drawerAccessor}.floorHeaderCode.layers.insulationLayer1`) || {};
        const { value: floorHeaderIns2Value } =
            useStateSelector(`${drawerAccessor}.floorHeaderCode.layers.insulationLayer2`) || {};
        const { value: floorHeaderSheathingValue } =
            useStateSelector(`${drawerAccessor}.floorHeaderCode.layers.sheathing`) || {};
        const { value: floorHeaderExteriorValue } =
            useStateSelector(`${drawerAccessor}.floorHeaderCode.layers.exterior`) || {};
        const floorHeaderLabel = useStateSelector(`${drawerAccessor}.floorHeaderCode.label`) || "";
        const floorHeaderLayers = useStateSelector(`${drawerAccessor}.floorHeaderCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.floorHeaderCode.layers`;

        const floorHeaderValue = `1${floorHeaderStructureValue
            .toString()
            .substr(
                -1
            )}${0}${0}${floorHeaderIns1Value}${floorHeaderIns2Value}${0}${floorHeaderSheathingValue}${floorHeaderExteriorValue}${0}`;

        const { codeRef: editingCodeRef = null, isLibCode = false } = floorHeaderInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("FloorHeader", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        useEffect(() => {
            const initialValues = {
                floorHeaderCode: isEmpty(floorHeaderInsType) ? floorHeaderCodeTemplate : floorHeaderInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = floorHeaderValue;
        }, [floorHeaderValue, floorHeaderLabel]);

        let availableComponents = getAllSubcomponents("floorHeader", components, fieldAccessor).sort(codeSort);

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const floorHeaderEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow
                    gridTemplate={CodeTypeSelect || (!isCodeLibrary && !isUpgradeComponent) ? "1fr 1fr 1fr" : "1fr 1fr"}
                    style={{ alignItems: "flex-end" }}
                >
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.floorHeaderCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={floorHeaderLabel.toString() === valueRef.current ? floorHeaderValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.floorHeaderCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={floorHeaderValue}
                    />
                    {!isCodeLibrary && !isUpgradeComponent && (
                        <Field
                            component={MultiSelect}
                            name={`${drawerAccessor}.selectedComponents`}
                            label={`Apply to multiple components`}
                            options={availableComponents.map(({ disabled = false, label = "", ...rest }) => ({
                                ...rest,
                                disabled,
                                label: disabled ? `${label} (current component)` : label,
                            }))}
                            hasSelectAll
                        />
                    )}
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <CodeLibraryDestination
                                    drawerAccessor={drawerAccessor}
                                    editingCodeRef={editingCodeRef}
                                    isLibCode={isLibCode}
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderRadius: "4px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.floorHeaderCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={getOptions({ fieldKey: "csFloorHeaderStructureType" })}
                            validate={structureValidation}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "4px" }}
                        />
                    </InputRow>
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={floorHeaderLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorHeader"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorHeaderEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorHeaderCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csFloorHeaderInsLayer1" })}
                        validate={ins1Validation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={floorHeaderLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorHeader"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorHeaderEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorHeaderCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csFloorHeaderInsLayer2" })}
                        validate={ins2Validation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="sheathing"
                    layers={floorHeaderLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorHeader"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={floorHeaderEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorHeaderCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csFloorHeaderSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="exterior"
                    layers={floorHeaderLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="floorHeader"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...floorHeaderEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.floorHeaderCode.layers.exterior`}
                        label="Exterior"
                        placeholder="Choose Exterior"
                        options={getOptions({ fieldKey: "csFloorHeaderExterior" })}
                        validate={exteriorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={floorHeaderCode}
                    componentType={"floorHeader"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.floorHeaderCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "floorHeaderInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            floorHeaderCode = {},
            addToLibrary = false,
            orgLibraryId = "",
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: "FloorHeader",
            componentId,
            code: floorHeaderCode,
            inputName: "floorHeaderInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardFloorHeaderCode);
